import axios from 'axios';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { FaBan, FaCheck, FaEdit, FaExchangeAlt, FaTrash } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import MaterialDropdown from '../../components/materialDropdown/MaterialDropdown';
import DataTable from '../../components/table/DataTable';
import { useUser } from '../../context/UserContext';
import { CardData } from '../../interfaces/cards';
import { Deck } from '../../interfaces/decks';
import ConfirmDeletePopup from '../../popups/confirmDeletePopup.tsx/ConfirmDeletePopup';
import EditCardPopup from '../../popups/editCard/EditCardPopup';
import MoveCardPopup from '../../popups/moveCard/MoveCardPopup';
import './CardsPage.css';

const PAGE_SIZE = 20;

const CardsPage: React.FC = () => {
  const { userData } = useUser();
  const languageCode = userData?.learning_language_code;
  const [decks, setDecks] = useState<Deck[]>([]);
  const [selectedDeck, setSelectedDeck] = useState<number | null>(null);
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  const [cards, setCards] = useState<CardData[]>([]);
  const [totalCards, setTotalCards] = useState(0);
  const [page, setPage] = useState(1);
  const [cardToEdit, setCardToEdit] = useState<CardData>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [showEditPopup, setShowEditPopup] = useState<boolean>(false);
  const [showMovePopup, setShowMovePopup] = useState<boolean>(false);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchDecks = useCallback(async () => {
    try {
      const response = await axios.get(
        `/api/decks/language_code/${languageCode}`
      );
      const data: Deck[] = response.data;
      setDecks(data);
      const state = location.state as { selectedDeck?: number };
      if (location.state && state.selectedDeck) {
        setSelectedDeck(state.selectedDeck);
      } else {
        setSelectedDeck(data[0]?.id || null);
      }
    } catch (error) {
      console.error('Error fetching decks:', error);
    }
  }, [location.state, languageCode]);

  const fetchCards = useCallback(async () => {
    setSelectedCards([]);
    setCardToEdit(undefined);
    if (selectedDeck === null) return;

    try {
      setLoading(true);
      const response = await axios.get(`/api/cards/deck/${selectedDeck}`, {
        params: { page, page_size: PAGE_SIZE, search: debouncedSearch },
      });
      setCards(response.data.items);
      setTotalCards(response.data.total);
    } catch (error) {
      console.error('Error fetching cards:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDeck, page, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchDecks();
  }, [fetchDecks]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const handleAddCard = () => {
    if (selectedDeck !== null) {
      navigate(`/add-card`, { state: { deckId: selectedDeck } });
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleDeckChange = (id: string) => {
    setSelectedDeck(parseInt(id, 10));
    setPage(1);
    setSelectedCards([]);
    setCardToEdit(undefined);
  };

  const toggleCardSelection = (cardId: number) => {
    setSelectedCards((prev) =>
      prev.includes(cardId) ? prev.filter((id) => id !== cardId) : [...prev, cardId]
    );
  };

  const submitCardEdit = () => {
    setShowEditPopup(false);
    fetchCards();
  };

  const submitCardMove = () => {
    setShowMovePopup(false);
    fetchCards();
  };

  const confirmDeleteCard = async () => {
    if (selectedCards.length >= 1) {
      try {
        await axios.delete(`/api/cards`, { data: selectedCards });
        fetchCards();
      } catch (error) {
        console.error('Error deleting the cards:', error);
      }
    }
    setShowDeletePopup(false);
  };

  const handleDisableCards = async (disabled: boolean) => {
    try {
      await axios.patch('/api/cards/disable', {
        card_ids: selectedCards,
        disabled,
      });
      fetchCards();
    } catch (error) {
      console.error('Error updating cards:', error);
    }
  };

  const selectedCardObjects = cards.filter((c) => selectedCards.includes(c.id));
  const allSelectedDisabled =
    selectedCardObjects.length > 0 && selectedCardObjects.every((c) => c.disabled);

  const canEditCard = cardToEdit && languageCode && selectedCards.length === 1;
  const canMoveCard =
    selectedDeck && decks.length > 1 && languageCode && selectedCards.length >= 1;
  const canDeleteCard = selectedCards.length >= 1;
  const canToggleDisabled = selectedCards.length >= 1;

  return (
    <>
      <div className="phn-page-container">
        <h1>Cards</h1>
        <div className="phn-vertical-container">
          <div className="phn-horizontal-group">
            <MaterialDropdown
              label="Deck"
              value={selectedDeck || ''}
              options={decks.map((deck) => ({ value: deck.id, label: deck.name }))}
              onChange={(value) => handleDeckChange(value)}
              className="cards-deck-select"
              disabled={decks.length === 0}
              fullWidth={false}
            />
            <button
              className="phn-button"
              onClick={handleAddCard}
              disabled={!selectedDeck}
            >
              Add Card
            </button>
          </div>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="phn-input "
          />
          <div className="phn-horizontal-group cards-action-buttons">
            <button
              className="phn-button-small"
              disabled={!canEditCard}
              onClick={(e) => {
                e.stopPropagation();
                setShowEditPopup(true);
              }}
            >
              Edit <FaEdit />
            </button>
            <button
              className="phn-button-small"
              disabled={!canMoveCard}
              onClick={(e) => {
                e.stopPropagation();
                setShowMovePopup(true);
              }}
            >
              Move <FaExchangeAlt />
            </button>
            <button
              className="phn-button-small"
              disabled={!canDeleteCard}
              onClick={(e) => {
                e.stopPropagation();
                setShowDeletePopup(true);
              }}
            >
              Delete <FaTrash />
            </button>
            <button
              className="phn-button-small"
              disabled={!canToggleDisabled}
              onClick={(e) => {
                e.stopPropagation();
                handleDisableCards(!allSelectedDisabled);
              }}
            >
              {allSelectedDisabled ? <>Enable <FaCheck /></> : <>Disable <FaBan /></>}
            </button>
          </div>
          <div className="cards-data-table-container">
            <DataTable
              headers={['Phrase', 'Meaning', 'Card Type', 'Next Review']}
              rows={cards.map((card) => [
                card.target_language,
                card.native_language,
                card.card_type,
                card.next_review_at
                  ? format(parseISO(card.next_review_at), 'MM/dd/yy')
                  : '',
              ])}
              widths={['auto', 'auto', '17%', '14%']}
              onRowClick={(index) => {
                toggleCardSelection(cards[index].id);
                setCardToEdit(cards[index]);
              }}
              isSelectable={true}
              isRowSelected={(index) => selectedCards.includes(cards[index]?.id)}
              isRowDisabled={(index) => cards[index]?.disabled ?? false}
              loading={loading}
              itemsPerPage={PAGE_SIZE}
              page={page}
              onPageChange={setPage}
              totalItems={totalCards}
            />
          </div>
        </div>
      </div>
      {canEditCard && showEditPopup && (
        <EditCardPopup
          card={cardToEdit}
          language_code={languageCode}
          onClose={() => setShowEditPopup(false)}
          onSubmit={submitCardEdit}
        />
      )}
      {canMoveCard && showMovePopup && (
        <MoveCardPopup
          cards={selectedCards}
          deck={selectedDeck}
          language={languageCode}
          onClose={() => setShowMovePopup(false)}
          onSubmit={submitCardMove}
        />
      )}
      {canDeleteCard && showDeletePopup && (
        <ConfirmDeletePopup
          onClose={() => setShowDeletePopup(false)}
          onConfirm={confirmDeleteCard}
          message="Are you sure you want to delete the selected cards?"
        />
      )}
    </>
  );
};

export default CardsPage;
